import {useLiveShoppingPlayerApi} from '@aboutyou/legacy-shared/src/pages/Live/context/LiveShoppingPlayerProvider';
import {pickLiveShowToAutoplay} from '@aboutyou/legacy-shared/src/pages/Live/util/pickLiveShowToAutoplay';
import {useEffect} from 'react';
import {useLocation} from 'react-router-dom';
import {DetailedDrop_LiveShow_LiveShowState} from '../../../../grpc_client/grips/protos/shared/detailed_drop';
import {useLocalStorage} from '../../../../utils/hooks/useLocalStorage';

export type LiveShowState = 'live' | 'scheduled' | 'ended';

export interface LiveShow {
  id: string;
  state: LiveShowState;
}

interface Props {
  liveShows: LiveShow[];
  skipLiveShowAlreadyPlayed?: boolean;
}

/**
 * This hook allow to automatically auto play a live show item. The first live show item with state "live" will be autoplayed (handled by pickLiveShowToAutoplay helper).
 * Remember to use it within the LiveShoppingPlayerProvider.
 * @param liveShows All the available live shows items.
 * @returns A function that allow to update the live shows to play.
 */
let currentLiveShowId = '';

export const useAutoplayLiveShow = ({
  liveShows,
  skipLiveShowAlreadyPlayed,
}: Props) => {
  const {player, updateShowIdAndAutoplay} = useLiveShoppingPlayerApi();
  const [liveShowClosed, setLiveShowClosed] = useLocalStorage<string[]>(
    'liveshow-closed',
    () => [],
  );
  const {search} = useLocation();

  player?.on(player.EVENT.CLOSE, () => {
    if (skipLiveShowAlreadyPlayed) {
      setLiveShowClosed([...liveShowClosed, currentLiveShowId]);
    }
  });

  useEffect(() => {
    const liveShowToAutoPlay = pickLiveShowToAutoplay(search, liveShows);

    if (!liveShowToAutoPlay || !updateShowIdAndAutoplay) {
      return;
    }

    const userSkipThisLiveShow =
      skipLiveShowAlreadyPlayed && liveShowClosed.includes(liveShowToAutoPlay);

    const shouldPlayLiveShow =
      currentLiveShowId !== liveShowToAutoPlay && !userSkipThisLiveShow;

    if (shouldPlayLiveShow) {
      updateShowIdAndAutoplay(liveShowToAutoPlay);
      currentLiveShowId = liveShowToAutoPlay;
    }
  }, [
    liveShowClosed,
    liveShows,
    search,
    skipLiveShowAlreadyPlayed,
    updateShowIdAndAutoplay,
  ]);

  return {updateShowIdAndAutoplay};
};

export const normalizeLiveShowState = (
  state: DetailedDrop_LiveShow_LiveShowState,
): LiveShowState => {
  const map: Record<number, LiveShowState> = {
    1: 'scheduled',
    2: 'live',
    3: 'ended',
  };

  return map[state];
};
